import { ChangeEvent, DragEvent, MouseEvent, forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { FileEntry } from "capitalrx-components-2.0/lib/forms/FileUpload/FileEntry";
import { InputProps } from "capitalrx-components-2.0/lib/forms/FileUpload/Input";
import { Placeholder } from "capitalrx-components-2.0/lib/forms/FileUpload/Placeholder";
import styles from "capitalrx-components-2.0/lib/forms/FileUpload/styles.module.scss";
import { useInputDisabled, useInputReadonly } from "capitalrx-components-2.0/lib/forms/InputGroup";
import { useSubmitting } from "capitalrx-components-2.0/lib/forms/SubmitGroup";
import { InputContainer } from "capitalrx-components-2.0/lib/forms/shared";
import { Button, CloseButtonAppearance, Icon, IconColor, IconName, LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { useId } from "capitalrx-components-2.0/lib/hooks/useId";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";
import { EmptyArray } from "capitalrx-components-2.0/lib/utilities/EmptyArray";
import { formatBytes } from "capitalrx-components-2.0/lib/utilities/fileSize";
import { getBasePropsFromHtmlProps } from "capitalrx-components-2.0/lib/utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "capitalrx-components-2.0/lib/utilities/getHtmlPropsFromBaseProps";
import classNames from "classnames";

const TRANSLATION_PATH = "FILE_UPLOAD";

function isFileSizeValid(file: File, maxSize: number | undefined): boolean {
  return maxSize === undefined || (file.size ?? 0) <= maxSize;
}

function isFileExtensionValid(file: File, allowedExtensions: ReadonlyArray<string> | undefined): boolean {
  const splittedName = file.name.split(".");

  if (splittedName.length < 2) {
    return false;
  }

  const extension = splittedName.pop();

  if (extension === undefined) {
    return allowedExtensions === undefined;
  }

  return (
    allowedExtensions === undefined
    || allowedExtensions
      .map((ext) => (ext.startsWith(".") ? ext.substring(1).toLowerCase() : ext.toLowerCase()))
      .includes(extension.toLowerCase())
  );
}

export const Input = forwardRef<HTMLDivElement, InputProps>(function Input(props, ref) {
  const {
    label,
    required = false,
    tooltip,
    hideLabel = false,
    value = EmptyArray as ReadonlyArray<FileEntry>,
    allowMultiple = false,
    maxSize,
    allowedExtensions,
    onChange,
    onDelete,
    tabIndex = -1,
    onFocusEnter,
    onFocusExit,
  } = props;

  const [dropping, setDropping] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const id = useId();
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();
  const disabled = useInputDisabled(props);

  const { t: translate } = useTranslation();

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.FileUpload, "file-upload", { dropping, disabled }],
    testIdPrefix: "file-upload",
  });

  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const cancelHandler = useCallback(() => {
    if (uploadInputRef?.current?.files?.length === 0) {
      onFocusExit?.();
    }
  }, [uploadInputRef?.current]);

  useEffect(() => {
    uploadInputRef.current?.addEventListener("cancel", cancelHandler);

    return () => uploadInputRef.current?.removeEventListener("cancel", cancelHandler);
  }, [uploadInputRef?.current]);

  const uploadTargetClickHandler = useCallback(
    (ev: MouseEvent) => {
      const { button } = ev;
      if (button !== 0) return;
      if (disabled) return;

      uploadInputRef?.current?.click?.();
    },
    [disabled],
  );

  const processFiles = useCallback(
    (files: FileList): ReadonlyArray<FileEntry> | null => {
      if (files.length === 0) return value;

      let result = value;

      if (allowMultiple) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (!file) continue;
          if (!isFileExtensionValid(file, allowedExtensions)) continue;

          result = [...(result || (EmptyArray as ReadonlyArray<FileEntry>)), { name: file.name, type: file.type, file }];
        }
      } else {
        const file = Array.from(files).find((file) => isFileExtensionValid(file, allowedExtensions));
        if (!file) return value;
        result = [{ name: file.name, type: file.type, file: file }];
      }

      return result;
    },
    [value, allowedExtensions, allowMultiple],
  );

  const fileChangeHandler = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      const { target } = ev;
      const { files } = target;

      if (!files) return;

      const newValue = processFiles(files);

      target.value = "";
      if (newValue === value) return;

      onChange?.(newValue, value);
    },
    [value, processFiles, onChange],
  );

  const removeAttachmentClickHandler = useCallback(
    async (entry: FileEntry) => {
      if (disabled) return;

      const { id } = entry;
      if (id !== undefined) {
        try {
          await onDelete?.(id);
        } catch {
          return;
        }
      }

      const newValue = (value || EmptyArray).filter((o) => o !== entry);
      onChange?.(newValue, value);

      onFocusExit?.();
    },
    [value, onDelete, onChange, disabled],
  );

  const dragEnterHandler = useCallback(
    (ev: DragEvent) => {
      ev.stopPropagation();
      ev.preventDefault();
    },
    [disabled],
  );

  const dragOverHandler = useCallback(
    (ev: DragEvent) => {
      ev.stopPropagation();
      ev.preventDefault();
      setDropping(true);
    },
    [allowMultiple, disabled],
  );

  const dragLeaveHandler = useCallback((ev: DragEvent) => {
    ev.stopPropagation();
    ev.preventDefault();
    setDropping(false);
  }, []);

  const dropHandler = useCallback(
    (ev: DragEvent) => {
      ev.stopPropagation();
      ev.preventDefault();
      setDropping(false);

      if (disabled) return;

      const { dataTransfer } = ev;
      if (!dataTransfer) return;

      const { files } = dataTransfer;

      const newValue = processFiles(files);

      if (newValue === value) return;

      containerRef.current?.focus();
      onChange?.(newValue, value);
    },
    [value, processFiles, onChange, disabled],
  );

  const focusHandler = useCallback(() => {
    if (disabled) return;
    onFocusEnter?.();
  }, [disabled]);

  const blurHandler = useCallback(() => {
    if (disabled) return;
    onFocusExit?.();
  }, [disabled]);

  const refHandler = useCallback(
    (innerRef: HTMLDivElement | null) => {
      containerRef.current = innerRef;

      if (ref) {
        if (ref instanceof Function) {
          ref(innerRef);
        } else {
          ref.current = innerRef;
        }
      }
    },
    [ref],
  );

  let normalizedExtensions: ReadonlyArray<string> | undefined = undefined;
  if (allowedExtensions && allowedExtensions.length > 0) {
    normalizedExtensions = allowedExtensions.map((extension) => {
      if (extension.startsWith(".")) return extension;
      else return `.${extension}`;
    });
  }

  const acceptList = normalizedExtensions ? normalizedExtensions.join(",") : undefined;

  let extensionsMessage = "";
  if (normalizedExtensions && normalizedExtensions.length > 0) {
    extensionsMessage = `${translate(`${TRANSLATION_PATH}.supportedFileTypes`, { count: normalizedExtensions.length })} `;
    extensionsMessage += normalizedExtensions.slice(0, -1).join(", ");

    if (normalizedExtensions.length > 1) {
      extensionsMessage += ` ${translate(`${TRANSLATION_PATH}.and`)} `;
    }

    extensionsMessage += `${normalizedExtensions[normalizedExtensions.length - 1]}.`;
  }

  let fileSizeMessage = "";
  if (maxSize !== undefined) {
    fileSizeMessage = `${translate(`${TRANSLATION_PATH}.maxFileSize`)} ${formatBytes(maxSize).toLowerCase().replace(" ", "")}. `;
  }

  const limitationMessage = maxSize || extensionsMessage ? `${fileSizeMessage}${extensionsMessage}` : undefined;

  return (
    <LoadingGroup.Placeholder
      fallback={<Placeholder className={props.className} style={props.style} hasInstructions={!!limitationMessage} />}
    >
      <Stack {...getBasePropsFromHtmlProps(baseHtmlProps)}>
        <InputContainer
          id={id}
          label={label}
          instructions={limitationMessage}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          tooltip={tooltip}
          hideLabel={hideLabel}
        >
          {!readOnly && (
            <div
              ref={refHandler}
              className="upload-target-container"
              onClick={uploadTargetClickHandler}
              onDragEnter={dragEnterHandler}
              onDragOver={dragOverHandler}
              onDragLeave={dragLeaveHandler}
              onDrop={dropHandler}
              onFocus={focusHandler}
              onBlur={blurHandler}
              tabIndex={tabIndex}
            >
              <Stack className="upload-target" alignment="center" spacing="small">
                <Icon name="upload" color={disabled ? "lightgrey" : "turquoise"} size="regular" />
                <Paragraph>{translate(`${TRANSLATION_PATH}.dragAndDropHere`, { count: allowMultiple ? 2 : 1 })}</Paragraph>
              </Stack>
              <input
                id={id}
                ref={uploadInputRef}
                type="file"
                multiple={allowMultiple}
                accept={acceptList}
                onChange={fileChangeHandler}
                disabled={disabled}
                readOnly={readOnly || submitting}
                hidden
              />
            </div>
          )}
          {readOnly && (!value || value.length === 0) && (
            <Stack className="files" spacing="small">
              <Stack className="file" spacing="none">
                <Shelf spacing="small">
                  <Icon name="attachment" color="black" />
                  <span>{translate(`${TRANSLATION_PATH}.noFilesAttached`)}</span>
                  <Shelf.Spacer />
                </Shelf>
              </Stack>
            </Stack>
          )}
        </InputContainer>
        {value && value.length > 0 && (
          <Stack className="files" spacing="small">
            {(value || (EmptyArray as ReadonlyArray<FileEntry>)).map((entry, index) => {
              const fileSizeValid = entry.file ? isFileSizeValid(entry.file, maxSize) : true;
              const valid = fileSizeValid;

              let iconName: IconName = !valid ? "warning-circle" : "attachment";
              let iconColor: IconColor = !valid ? "red" : "black";
              if (disabled) {
                iconName = "attachment";
                iconColor = "lightgrey";
              }

              return (
                <Stack key={index} className={classNames("file", { invalid: !valid })} spacing="none">
                  <Shelf spacing="small">
                    <Icon name={iconName} color={iconColor} />
                    <span>{entry.name}</span>
                    <Shelf.Spacer />
                    {((onDelete && entry.id !== undefined) || entry.id === undefined) && !readOnly && (
                      <Button
                        icon={onDelete && entry.id !== undefined ? "delete" : "x"}
                        appearance={CloseButtonAppearance}
                        tooltip={translate(`${TRANSLATION_PATH}.removeFile`)}
                        size="small"
                        disabled={disabled}
                        onClick={() => removeAttachmentClickHandler(entry)}
                      />
                    )}
                  </Shelf>
                  {!fileSizeValid && !disabled && (
                    <Paragraph className="error-message">
                      {translate(`${TRANSLATION_PATH}.fileCannotBeLargerThan`)}{" "}
                      {formatBytes(maxSize ?? 0)
                        .toLowerCase()
                        .replace(" ", "")}
                      .
                    </Paragraph>
                  )}
                </Stack>
              );
            })}
          </Stack>
        )}
      </Stack>
    </LoadingGroup.Placeholder>
  );
});
